import { MathUtils, Vector4 } from 'three';
import { Alignment, AnimationPathType, BorderSide, ClusterMode, isPlanogramVersionCluster, isPlanogramVersionShape, isPlanogramVersionTextArea, TextSizingMode, } from './planogram';
import { PLANOGRAM_CONFIG } from '../config/PlanogramConfig';
import { assertDefined } from '../utils/debug';
import { bytesArrayToHex, rgbArrayToHex } from '../utils/colors';
import { computeFontProperties } from '../utils/font-utils';
const requiredItemProperties = [
    'id',
    'x',
    'y',
    'width',
    'height',
    'type',
];
function migrateCluster(item) {
    if (item.data.fillColor === undefined) {
        item.data = Object.assign(Object.assign({}, item.data), DEFAULT_CLUSTER_SHAPE_DATA);
    }
    else if (item.data.distributeEqually === undefined) {
        item.data = Object.assign(Object.assign({}, item.data), { distributeEqually: false, spacing: item.data.clusterMode === ClusterMode.Grid ? item.data.padding : 0, padding: {
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            }, clusterMode: item.data.clusterMode, equalSizeObjects: item.data.clusterMode === ClusterMode.Grid, verticalAlignment: Alignment.START, horizontalAlignment: Alignment.START });
    }
}
function migrateShape(item) {
    if (item.data.individualCornerRadius === undefined) {
        assertDefined(item.data.cornerRadius, 'Item has no corner radius defined');
        const relativeCornerRadius = item.data.cornerRadius / Math.min(item.width, item.height);
        item.data.individualCornerRadius = new Vector4(1, 1, 1, 1)
            .multiplyScalar(relativeCornerRadius)
            .toArray();
    }
    if (item.data.borderSide === undefined)
        item.data.borderSide = BorderSide.Centered;
    if (typeof item.data.borderColor === 'object') {
        item.data.borderOpacity = item.data.borderColor[3];
        item.data.borderColor = bytesArrayToHex(item.data.borderColor);
    }
    if (typeof item.data.fillColor === 'object') {
        item.data.fillOpacity = item.data.fillColor[3];
        item.data.fillColor = bytesArrayToHex(item.data.fillColor);
    }
}
function migrateText(item) {
    var _a, _b, _c, _d;
    const data = item.data;
    if (data.fontFamily !== undefined &&
        (data.fontFamily.fontWeightOptions === undefined ||
            data.fontFamily.display_font_family === undefined ||
            data.fontFamily.computedWeight === undefined)) {
        Object.assign(data, {
            fontFamily: Object.assign(Object.assign({}, data.fontFamily), computeFontProperties(data.fontFamily)),
        });
    }
    if (data.resizingMode === undefined ||
        data.autoFit === undefined ||
        data.overflowWrap === undefined ||
        data.effectiveFontSize === undefined) {
        Object.assign(data, {
            resizingMode: (_a = data.resizingMode) !== null && _a !== void 0 ? _a : TextSizingMode.FIXED,
            autoFit: (_b = data.autoFit) !== null && _b !== void 0 ? _b : false,
            overflowWrap: (_c = data.overflowWrap) !== null && _c !== void 0 ? _c : 'break-word',
            effectiveFontSize: (_d = data.effectiveFontSize) !== null && _d !== void 0 ? _d : data.fontSize,
        });
    }
    return Object.assign(Object.assign({}, item), { data });
}
export function migrateFabricStateItems(items) {
    return items
        .filter(item => {
        const hasAllRequiredProperties = requiredItemProperties.every(property => item[property] !== undefined && item[property] !== null);
        if (!hasAllRequiredProperties) {
            console.error('Item missing required properties:', item);
        }
        return hasAllRequiredProperties;
    })
        .map(item => {
        if (isPlanogramVersionCluster(item))
            migrateCluster(item);
        if (isPlanogramVersionCluster(item) || isPlanogramVersionShape(item))
            migrateShape(item);
        if (isPlanogramVersionTextArea(item))
            migrateText(item);
        return item;
    });
}
const FABRIC_STATE_DEFAULTS = {
    sphereConfig: {
        horizonLine: PLANOGRAM_CONFIG.horizonLine,
        startingPoint: PLANOGRAM_CONFIG.startingPoint,
        backgroundColor: '#fff',
        clustersOrder: [],
        iframesOrder: [],
        animationPaths: [],
    },
    editorConfig: {
        templateFontMappings: undefined,
        showGrid: false,
        disableClusters: false,
        lockedObjects: [],
    },
    items: [],
    figmaBackground: undefined,
};
export const migrateFabricState = (fabricState) => {
    var _a, _b, _c;
    const items = fabricState.items !== undefined ? migrateFabricStateItems(fabricState.items) : [];
    const animationPaths = ((_b = (_a = fabricState.sphereConfig) === null || _a === void 0 ? void 0 : _a.animationPaths) !== null && _b !== void 0 ? _b : FABRIC_STATE_DEFAULTS.sphereConfig.animationPaths).map(path => {
        var _a, _b, _c;
        return (Object.assign(Object.assign({}, path), { uuid: (_a = path.uuid) !== null && _a !== void 0 ? _a : MathUtils.generateUUID(), type: path.type === undefined
                ? path.autoplay === false
                    ? AnimationPathType.NavigationArrows
                    : AnimationPathType.Custom
                : path.type, seoTitle: (_b = path.seoTitle) !== null && _b !== void 0 ? _b : '', seoDescription: (_c = path.seoDescription) !== null && _c !== void 0 ? _c : '' }));
    });
    let backgroundColor = (_c = fabricState.sphereConfig) === null || _c === void 0 ? void 0 : _c.backgroundColor;
    if (Array.isArray(backgroundColor)) {
        backgroundColor = rgbArrayToHex(backgroundColor);
    }
    return Object.assign(Object.assign({}, fabricState), { sphereConfig: Object.assign(Object.assign(Object.assign({}, FABRIC_STATE_DEFAULTS.sphereConfig), fabricState.sphereConfig), { animationPaths, backgroundColor: backgroundColor }), editorConfig: Object.assign(Object.assign({}, FABRIC_STATE_DEFAULTS.editorConfig), fabricState.editorConfig), items, figmaBackground: fabricState.figmaBackground });
};
export const DEFAULT_CLUSTER_SHAPE_DATA = {
    thickness: 0,
    soft: false,
    dashed: false,
    borderColor: '#000',
    borderOpacity: 0,
    fillColor: '#000',
    fillOpacity: 0,
    individualCornerRadius: [0, 0, 0, 0],
    borderSide: BorderSide.Centered,
};
